import { db, storage } from "../firebase";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { uploadOptimizedImage } from "@/lib/uploadImage";
import { sortByOrder } from "./reorderService";

const ACCOMMODATION_COLLECTION = "accommodation";

/** Generates a fresh id for a new accommodation draft — reused across image
 * upload + final submit, same pattern as generateFerryId/generateTransportId. */
export function generateAccommodationId() {
  return doc(collection(db, ACCOMMODATION_COLLECTION)).id;
}

export async function createAccommodation(id, payload) {
  await setDoc(doc(db, ACCOMMODATION_COLLECTION, id), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return id;
}

export async function updateAccommodation(id, payload) {
  await updateDoc(doc(db, ACCOMMODATION_COLLECTION, id), {
    ...payload,
    updatedAt: serverTimestamp(),
  });
  return id;
}

export async function deleteAccommodation(id, imageUrl) {
  await deleteDoc(doc(db, ACCOMMODATION_COLLECTION, id));
  if (imageUrl) {
    try {
      await deleteObject(ref(storage, imageUrl));
    } catch (err) {
      // Image may already be gone or URL may not be a storage ref
      // (e.g. external URL) — don't block the delete on cleanup failing.
      console.warn("Accommodation image cleanup failed:", err);
    }
  }
}

export async function getAllAccommodation() {
  const snap = await getDocs(collection(db, ACCOMMODATION_COLLECTION));
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return sortByOrder(items);
}

/** Uploads/replaces the main photo for an accommodation listing. If
 * replacing, the old image at existingUrl is deleted after the new one
 * succeeds. */
export async function uploadAccommodationImage(id, file, existingUrl) {
  const storageRef = ref(
    storage,
    `accommodation/${id}/${Date.now()}_${file.name}`,
  );
  const snap = await uploadOptimizedImage(storageRef, file);
  const url = await getDownloadURL(snap.ref);

  if (existingUrl) {
    try {
      await deleteObject(ref(storage, existingUrl));
    } catch (err) {
      console.warn("Old accommodation image cleanup failed:", err);
    }
  }

  return url;
}